class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    // Seth 2026-10-13 audit (K15): keep the form this listener belongs to. The submit runs 800 ms after a change and
    // used to look the form up from the changed box, which refreshed filters may have replaced by then.
    const facetForm = this.querySelector('form');
    this.facetForm = facetForm;
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    // Seth 2026-10-13 audit: note the filter group of every change (capture phase, because the typed size boxes stop
    // their own input events), so results asked for before that change do not overwrite it.
    facetForm.addEventListener('input', FacetFiltersForm.noteChange, true);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      // Seth 2026-10-13 audit: a submit still waiting from a change made before Back would push a page over the one
      // Back draws; onSubmitHandler skips it until the shopper changes something again.
      FacetFiltersForm.changeCountAtBack = FacetFiltersForm.changeCount;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // Seth 2026-10-13 audit: changes are numbered, and each filter group (or the sort menu) keeps the number of its last one.
  static noteChange(event) {
    const group = event.target.closest('.js-filter, .sorting');
    if (group) FacetFiltersForm.changedGroups.set(group, ++FacetFiltersForm.changeCount);
  }

  // True when the shopper changed this group after a request that holds the first `changeCount` changes was sent.
  static changedSince(element, changeCount) {
    return Boolean(element) && (FacetFiltersForm.changedGroups.get(element) || 0) > changeCount;
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    // Seth 2026-10-13 audit: number every request and note how many changes it holds. Only the newest request draws
    // its results, so a late answer never undoes a newer choice, and a group changed after now keeps that change.
    const request = { id: ++FacetFiltersForm.requestCount, changeCount: FacetFiltersForm.changeCount };
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    // Seth 2026-10-13 audit (K15): no error on a page without the product grid
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, request);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent, request) {
    fetch(url)
      .then((response) => {
        // Seth 2026-10-13 audit: a refused request (the store's bot check answers 429 with its own page) was drawn as
        // results, which removed every filter group. Keep the filters and doors on screen instead.
        if (!response.ok) throw new Error(`Filter results did not load (HTTP ${response.status})`);
        return response.text();
      })
      .then((html) => {
        // Seth 2026-10-13 audit: an answer that is not the filter section (a challenge, error or password page served
        // as 200) is refused the same way. Every real answer holds the grid's container, with doors or without.
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        if (!parsedHTML.getElementById('ProductGridContainer')) {
          throw new Error('Filter results did not load (not the filter section)');
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        // Seth 2026-10-13 audit: a newer choice was made while this loaded; its own request draws the results.
        if (request.id !== FacetFiltersForm.requestCount) return FacetFiltersForm.resolveUpdateEvent(parsedHTML, updateEvent);
        FacetFiltersForm.renderSection(html, event, updateEvent, request.changeCount);
      })
      .catch((error) => {
        console.error(error);
        if (request.id === FacetFiltersForm.requestCount) FacetFiltersForm.endLoading();
        updateEvent?.reject(error);
      });
  }

  // Seth 2026-10-13 audit: Shopify's update event for results that were not drawn still gets their count.
  static resolveUpdateEvent(parsedHTML, updateEvent) {
    if (!updateEvent) return;
    const sourceCount = parsedHTML.getElementById('ProductCount');
    updateEvent.resolve(parseInt(sourceCount?.dataset.productCount) || 0);
  }

  // Seth 2026-10-13 audit: when a request fails, the doors and filters stay as they were; end the loading look.
  static endLoading() {
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
    document.getElementById('ProductCount')?.classList.remove('loading');
    document.getElementById('ProductCountDesktop')?.classList.remove('loading');
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent, changeCount = FacetFiltersForm.changeCount) {
    FacetFiltersForm.renderFilters(html, event, changeCount);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    FacetFiltersForm.renderApplyCounts(parseInt(sourceCount.dataset.productCount, 10));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  // MAD (2026-09-25): the phone drawer's Apply buttons say how many results there are
  // (plan 4.4). The wording comes from snippets/mad-apply-label.liquid (data-one,
  // data-other, data-zero); only the number changes here, with a thousands comma
  // like the snippet prints it (QA K-01).
  static renderApplyCounts(count) {
    if (!Number.isFinite(count) || count < 0) return;
    const shown = String(Math.floor(count)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    document.querySelectorAll('[data-mad-apply-count]').forEach((label) => {
      const template = count === 0 ? label.dataset.zero : count === 1 ? label.dataset.one : label.dataset.other;
      if (template) label.textContent = template.replace('[count]', shown);
    });
  }

  static renderFilters(html, event, changeCount = FacetFiltersForm.changeCount) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    // Seth 2026-10-13 audit: a group the shopper changed after this request was sent, or is typing a size in now,
    // keeps what they chose (it used to untick, or the typed size reset); the request their change makes follows.
    const active = document.activeElement;
    const typingIn =
      event && active && active.tagName === 'INPUT' && /^(text|number|search)$/.test(active.type) ? active : null;
    // Seth 2026-10-13 audit: a kept group is remembered, as its Clear and Reset links keep an older answer's filters
    const keep = (element) => {
      const kept = FacetFiltersForm.changedSince(element, changeCount) || Boolean(typingIn && element.contains(typingIn));
      if (kept) FacetFiltersForm.keptGroups.add(element);
      return kept;
    };

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        if (!keep(currentElement)) currentElement.remove();
      }
    });

    // Seth 2026-10-13 audit (K15): the changed box's group, read once; null when that box has left the page
    const jsFilter = event ? event.target.closest('.js-filter') : null;
    const matchesId = (element) => (jsFilter ? element.id === jsFilter.id : false);

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        if (keep(currentElement)) return;
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML, changeCount);

    if (countsToRender && !FacetFiltersForm.changedSince(jsFilter, changeCount)) {
      const closestJSFilterID = jsFilter.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, jsFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
        // Seth 2026-10-13 audit: a group kept from an older answer takes this answer's Clear and Reset links, once
        if (FacetFiltersForm.keptGroups.delete(jsFilter)) FacetFiltersForm.renderKeptLinks(countsToRender, jsFilter);

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        // Seth 2026-10-13 audit: focus stays put while the shopper types, or once they have made another change
        if (!isTextInput && !typingIn && FacetFiltersForm.changeCount === changeCount) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html, changeCount) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      // Seth 2026-10-13 audit: keep a sort chosen after this request was sent
      if (FacetFiltersForm.changedSince(document.querySelector(selector), changeCount)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  // Seth 2026-10-13 audit: the Clear link in a phone drawer group's footer and a size group's Reset link, copied from
  // the answer. Only the Clear link: the footer's Apply button keeps the drawer's listener (bindEvents ran already)
  // and its count is rewritten anyway. The Reset link is there only while that size has a value, so it may come or go.
  static renderKeptLinks(source, target) {
    const targetClear = target.querySelector('.mobile-facets__footer facet-remove');
    const sourceClear = source.querySelector('.mobile-facets__footer facet-remove');

    if (sourceClear && targetClear) {
      targetClear.outerHTML = sourceClear.outerHTML;
    }

    const targetRange = target.querySelector('.mad-range');
    if (!targetRange) return;
    targetRange.querySelector('.mad-range__reset')?.closest('facet-remove')?.remove();
    const sourceReset = source.querySelector('.mad-range__reset')?.closest('facet-remove');
    if (sourceReset) targetRange.appendChild(sourceReset);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    // Seth 2026-10-13 audit: no change since Back (setListeners), so this submit was left over from before it
    if (FacetFiltersForm.changeCount === FacetFiltersForm.changeCountAtBack) return;
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    // Seth 2026-10-13 audit (K15): the form this listener belongs to, as it is now. event.target.closest('form') is
    // null once refreshed filters have replaced the changed box (Safari: "null is not an object (evaluating
    // event.target.closest("form").id)"), which threw here and lost the shopper's second choice.
    const facetForm = this.facetForm;
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(facetForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = facetForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// Seth 2026-10-13 audit: the request and change numbers (renderPage, noteChange), the change number at the last Back
// that redrew (setListeners), and the groups kept from an answer (renderFilters)
FacetFiltersForm.requestCount = 0;
FacetFiltersForm.changeCount = 0;
FacetFiltersForm.changedGroups = new WeakMap();
FacetFiltersForm.changeCountAtBack = null;
FacetFiltersForm.keptGroups = new WeakSet();
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
